// web/src/components/schedule/MonthView.jsx
// The month grid — the "when is that clinic again" view. A cell can't show a
// month's worth of feed and turnout, so it shows the first few items and a
// count; the day itself is a link into the Day view, which has room for all of
// it.
//
// Days from the neighbouring months still render (the grid is whole weeks) but
// sit grayed, the way every calendar does it.
//
// Below md a seven-column text cell is under ~90px and truncates to nothing,
// and side-scrolling a month defeats the point of a month — you look at one to
// take in the shape of it at a glance. So the narrow cell keeps the grid and
// drops the words: a day number and a dot per item, emerald for tasks and
// indigo for lessons, grayed once done. That still answers "which days are
// busy" and "is there a lesson that week", and the detail is one tap away in
// the Day view either way.

import { itemsForDate, formatDay, formatTime } from '../../lib/schedule';

const PER_CELL = 3; // text rows, md and up
const MAX_DOTS = 4; // dots that fit a ~45px phone cell
const WEEKDAYS = [
  ['Mon', 'M'],
  ['Tue', 'T'],
  ['Wed', 'W'],
  ['Thu', 'T'],
  ['Fri', 'F'],
  ['Sat', 'S'],
  ['Sun', 'S'],
];

export default function MonthView({ weeks, month, today, tasks, lessons, services = [], onOpenDay }) {
  return (
    <div className="grid grid-cols-7 gap-1 md:gap-2">
      {WEEKDAYS.map(([long, short]) => (
        <div
          key={long}
          className="pb-1 text-center md:text-left md:px-1 text-[10px] md:text-xs font-semibold uppercase tracking-wide text-ink-400"
        >
          <span className="md:hidden" aria-hidden>{short}</span>
          <span className="hidden md:inline">{long}</span>
          {/* The single letters repeat (T, T / S, S); keep the full name for
              anyone listening rather than looking. */}
          <span className="sr-only md:hidden">{long}</span>
        </div>
      ))}

      {weeks.flat().map((date) => (
        <DayCell
          key={date}
          date={date}
          inMonth={date.slice(0, 7) === month}
          isToday={date === today}
          items={itemsForDate(date, tasks, lessons, services)}
          onOpen={() => onOpenDay(date)}
        />
      ))}
    </div>
  );
}

function DayCell({ date, inMonth, isToday, items, onOpen }) {
  const shown = items.slice(0, PER_CELL);
  const extra = items.length - shown.length;
  const dots = items.slice(0, MAX_DOTS);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${formatDay(date, { weekday: 'long', month: 'long', day: 'numeric' })} — ${
        items.length === 1 ? '1 item' : `${items.length} items`
      }`}
      className={
        'text-left card p-1 md:p-2 h-16 md:h-32 flex flex-col overflow-hidden transition hover:border-ink-300 ' +
        (isToday ? 'border-hunter bg-hunter/5 ' : '') +
        (inMonth ? '' : 'bg-ink-50/60 ')
      }
    >
      <div className="flex items-baseline justify-between mb-0.5 md:mb-1">
        <span
          className={
            'text-xs md:text-sm font-semibold ' +
            (isToday ? 'text-hunter' : inMonth ? 'text-ink-800' : 'text-ink-300')
          }
        >
          {formatDay(date, { day: 'numeric' })}
        </span>
        {items.length > 0 && (
          <span className="hidden md:inline text-[10px] text-ink-400 tabular-nums">
            {items.length}
          </span>
        )}
      </div>

      {/* Phone: density dots. */}
      <div className="md:hidden flex flex-wrap gap-0.5 content-start" aria-hidden>
        {dots.map((item) => (
          <span
            key={item.key}
            className={
              'w-1.5 h-1.5 rounded-full ' +
              (item.done
                ? 'bg-ink-200'
                : item.kind === 'task'
                  ? 'bg-emerald-400'
                  : 'bg-indigo-400')
            }
          />
        ))}
        {items.length > MAX_DOTS && (
          <span className="text-[9px] leading-none text-ink-400 tabular-nums">
            +{items.length - MAX_DOTS}
          </span>
        )}
      </div>

      {/* Wider than a phone: the actual items. */}
      <div className="hidden md:block flex-1 space-y-1 overflow-hidden">
        {shown.map((item) => (
          <div key={item.key} className="flex items-center gap-1">
            <span
              aria-hidden
              className={
                'shrink-0 w-1 h-3 rounded-full ' +
                (item.kind === 'task'
                  ? 'bg-emerald-400'
                  : item.kind === 'service'
                    ? 'bg-slate-400'
                    : 'bg-indigo-400')
              }
            />
            <span
              className={
                'truncate text-[11px] ' +
                (item.done ? 'text-ink-400 line-through' : 'text-ink-600')
              }
            >
              {item.time ? `${formatTime(item.time)} ` : ''}
              {item.title}
            </span>
          </div>
        ))}
        {extra > 0 && <div className="text-[11px] text-ink-400 pl-2">+{extra} more</div>}
      </div>
    </button>
  );
}
